import Pickr from "@simonwep/pickr";
import "@simonwep/pickr/dist/themes/monolith.min.css";
import { LinkIcon, UnlinkIcon } from "../../../editorjs/icons/icons";

export default class BackdropTune {
  static get isTune() {
    return true;
  }

  constructor({ api, data, config, block }) {
    this.api = api;
    this.block = block;
    this.settings = config;
    this._pickers = [];

    this.data = {
      padding: {
        top: data?.padding?.top ?? 0,
        right: data?.padding?.right ?? 0,
        bottom: data?.padding?.bottom ?? 0,
        left: data?.padding?.left ?? 0,
        linked: data?.padding?.linked ?? true,
      },
      backgroundColor: data?.backgroundColor ?? "#ffffff00",
      border: {
        top: data?.border?.top ?? 0,
        right: data?.border?.right ?? 0,
        bottom: data?.border?.bottom ?? 0,
        left: data?.border?.left ?? 0,
        color: data?.border?.color ?? "#00000000",
        linked: data?.border?.linked ?? true,
      },
      borderRadius: {
        topLeft: data?.borderRadius?.topLeft ?? 0,
        topRight: data?.borderRadius?.topRight ?? 0,
        bottomRight: data?.borderRadius?.bottomRight ?? 0,
        bottomLeft: data?.borderRadius?.bottomLeft ?? 0,
        linked: data?.borderRadius?.linked ?? true,
      },
      boxShadow: {
        x: data?.boxShadow?.x ?? 0,
        y: data?.boxShadow?.y ?? 0,
        blur: data?.boxShadow?.blur ?? 0,
        spread: data?.boxShadow?.spread ?? 0,
        color: data?.boxShadow?.color ?? "#00000000",
        inset: data?.boxShadow?.inset ?? false,
      },
    };

    this._CSS = {
      wrapper: "cdx-backdrop-settings",
      col: "cdx-backdrop-column",
      row: "cdx-backdrop-row",
      label: "cdx-backdrop-label",
      input: "cdx-backdrop-input",
      colorBtn: "cdx-backdrop-color-btn",
      linkBtn: "cdx-backdrop-link-btn",
      linkBtnActive: "cdx-backdrop-link-btn-active",
      checkbox: "cdx-backdrop-checkbox",

      tuneWrapper: "editor-block-tune",
      tuneTitle: "editor-block-tune-title",
      tuneText: "editor-block-tune-text",
      tuneInput: "editor-block-tune-input",
      tuneButton: "editor-block-tune-button",
      tuneContent: "editor-block-tune-content-container",
    };
  }

  wrap(blockContent) {
    this.wrapper = document.createElement("div");
    this.wrapper.classList.add("cdx-backdrop-container");
    this.wrapper.style.display = "flow-root";
    this.wrapper.append(blockContent);
    this._applyPadding();
    this._applyBackgroundColor();
    this._applyBorder();
    this._applyBorderRadius();
    this._applyBoxShadow();
    return this.wrapper;
  }

  _getTarget() {
    return (
      this.wrapper.closest(".cdx-dimension-block-wrapper") ??
      this.wrapper.querySelector(".cdx-dimension-block-wrapper") ??
      this.wrapper
    );
  }

  _applyPadding() {
    const target = this._getTarget();
    if (!target) return;

    ["top", "right", "bottom", "left"].forEach((side) => {
      const val = this.data.padding[side];
      target.style[`padding${side.charAt(0).toUpperCase() + side.slice(1)}`] =
        `${val || 0}px`;
    });
  }

  _applyBackgroundColor() {
    const target = this._getTarget();
    if (!target) return;
    target.style.backgroundColor = this.data.backgroundColor;
  }

  _applyBorder() {
    const target = this._getTarget();
    if (!target) return;

    ["top", "right", "bottom", "left"].forEach((side) => {
      const width = this.data.border[side];
      const prop = `border${side.charAt(0).toUpperCase() + side.slice(1)}`;
      target.style[prop] = `${width || 0}px solid ${this.data.border.color}`;
    });
  }

  _applyBorderRadius() {
    const target = this._getTarget();
    if (!target) return;

    const map = {
      topLeft: "borderTopLeftRadius",
      topRight: "borderTopRightRadius",
      bottomRight: "borderBottomRightRadius",
      bottomLeft: "borderBottomLeftRadius",
    };

    let highestVal = 0;
    Object.entries(map).forEach(([key, prop]) => {
      const val = this.data.borderRadius[key];
      target.style[prop] = `${val || 0}px`;
      if(val > highestVal){
        highestVal = val;
      }
    });
    if(highestVal > 0){
      target.style.overflow = "hidden";
    } else {
      target.style.overflow = "visible";
    }
  }

  _applyBoxShadow() {
    const target = this._getTarget();
    if (!target) return;

    const { x, y, blur, spread, color, inset } = this.data.boxShadow;
    target.style.boxShadow = `${inset ? "inset " : ""}${x || 0}px ${
      y || 0
    }px ${blur || 0}px ${spread || 0}px ${color}`;
  }

  debounce(func, timeout = 300) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  }

  /**
   * Builds a titled row of numeric side/corner inputs plus a link/unlink
   * toggle that keeps all values in sync while linked.
   */
  _buildLinkableGroup({
    title,
    sides,
    labels,
    dataTarget,
    applyFn,
    allowNegative = false,
  }) {
    const css = this._CSS;
    const container = document.createElement("div");
    container.classList.add(
      "cdx-backdrop-row",
      "d-flex",
      "editor-block-tune",
      "pe-3",
    );

    const groupTitle = document.createElement("label");
    groupTitle.classList.add(css.tuneTitle);
    groupTitle.innerHTML = title;

    const groupRow = document.createElement("div");
    groupRow.classList.add("d-flex", "align-items-end");

    const inputs = {};

    sides.forEach((side) => {
      const col = document.createElement("div");
      col.classList.add(css.col, "d-flex", "editor-block-tune");

      const label = document.createElement("label");
      label.classList.add(css.label, css.tuneTitle);
      label.innerHTML = labels[side];

      const input = document.createElement("input");
      input.classList.add(css.input, css.tuneInput);
      input.setAttribute("type", "number");
      if (!allowNegative) input.setAttribute("min", "0");
      input.dataset.side = side;

      const current = dataTarget[side];
      if (current) input.value = current;

      input.addEventListener("input", (e) => {
        const val = e.target.value === "" ? 0 : e.target.value;
        dataTarget[side] = val;

        if (dataTarget.linked) {
          sides.forEach((s) => {
            dataTarget[s] = val;
            if (inputs[s]) inputs[s].value = val || "";
          });
        }

        applyFn();
        this.debounce(() => this.block.dispatchChange())();
      });

      inputs[side] = input;

      col.appendChild(label);
      col.appendChild(input);
      groupRow.appendChild(col);
    });

    // Link/unlink toggle
    const linkCol = document.createElement("div");
    linkCol.classList.add("d-flex", "editor-block-tune");

    const linkLabel = document.createElement("label");
    linkLabel.classList.add(css.label, css.tuneTitle);
    linkLabel.innerHTML = "&nbsp;"; // spacer to align with input row

    const linkBtn = document.createElement("button");
    linkBtn.type = "button";
    linkBtn.classList.add(css.linkBtn, css.tuneButton, "cdx-settings-button");

    const updateLinkBtn = () => {
      linkBtn.setAttribute(
        "aria-pressed",
        dataTarget.linked ? "true" : "false",
      );
      linkBtn.title = dataTarget.linked ? "Unlink values" : "Link values";
      linkBtn.innerHTML = dataTarget.linked ? LinkIcon : UnlinkIcon;
      linkBtn.classList.toggle(css.linkBtnActive, dataTarget.linked);
    };
    updateLinkBtn();

    linkBtn.addEventListener("click", () => {
      dataTarget.linked = !dataTarget.linked;
      updateLinkBtn();

      if (dataTarget.linked) {
        // Snap all sides to the first side's value the moment linking is re-enabled
        const val = dataTarget[sides[0]] || 0;
        sides.forEach((s) => {
          dataTarget[s] = val;
          if (inputs[s]) inputs[s].value = val || "";
        });
        applyFn();
        this.debounce(() => this.block.dispatchChange())();
      }
    });

    linkCol.appendChild(linkLabel);
    linkCol.appendChild(linkBtn);
    groupRow.appendChild(linkCol);

    container.appendChild(groupTitle);
    container.appendChild(groupRow);

    return container;
  }

  _buildColorRow(labelText, colorInput) {
    const css = this._CSS;
    const row = document.createElement("div");
    row.classList.add(css.row, "d-flex", "editor-block-tune");

    const label = document.createElement("label");
    label.classList.add(css.label, css.tuneTitle);
    label.innerHTML = labelText;

    row.appendChild(label);
    row.appendChild(colorInput);
    return row;
  }

  render() {
    const css = this._CSS;
    const wrapper = document.createElement("div");
    // const tuneTitle = document.createElement("label");

    wrapper.classList.add(css.wrapper, css.tuneWrapper);
    // tuneTitle.classList.add(css.tuneTitle);
    // tuneTitle.innerHTML = "Block backdrop";
    // wrapper.appendChild(tuneTitle);

    // Padding
    wrapper.appendChild(
      this._buildLinkableGroup({
        title: "Padding",
        sides: ["top", "right", "bottom", "left"],
        labels: { top: "Top", right: "Right", bottom: "Bottom", left: "Left" },
        dataTarget: this.data.padding,
        applyFn: () => this._applyPadding(),
      }),
    );

    // Background colour
    const bgColorInput = document.createElement("input");
    bgColorInput.classList.add(css.colorBtn);
    wrapper.appendChild(this._buildColorRow("Background colour", bgColorInput));

    // Border widths
    wrapper.appendChild(
      this._buildLinkableGroup({
        title: "Border",
        sides: ["top", "right", "bottom", "left"],
        labels: { top: "Top", right: "Right", bottom: "Bottom", left: "Left" },
        dataTarget: this.data.border,
        applyFn: () => this._applyBorder(),
      }),
    );

    // Border colour
    const borderColorInput = document.createElement("input");
    borderColorInput.classList.add(css.colorBtn);
    wrapper.appendChild(this._buildColorRow("Border colour", borderColorInput));

    // Border radius
    wrapper.appendChild(
      this._buildLinkableGroup({
        title: "Border radius",
        sides: ["topLeft", "topRight", "bottomRight", "bottomLeft"],
        labels: {
          topLeft: "Top L",
          topRight: "Top R",
          bottomRight: "Bottom R",
          bottomLeft: "Bottom L",
        },
        dataTarget: this.data.borderRadius,
        applyFn: () => this._applyBorderRadius(),
      }),
    );

    // Box shadow
    wrapper.appendChild(this._buildBoxShadowGroup());

    // Wire up all three color pickers
    this._initColorPicker(bgColorInput, wrapper, {
      getVal: () => this.data.backgroundColor,
      setVal: (v) => {
        this.data.backgroundColor = v;
      },
      applyFn: () => this._applyBackgroundColor(),
    });

    this._initColorPicker(borderColorInput, wrapper, {
      getVal: () => this.data.border.color,
      setVal: (v) => {
        this.data.border.color = v;
      },
      applyFn: () => this._applyBorder(),
    });

    return wrapper;
  }

  _buildBoxShadowGroup() {
    const css = this._CSS;
    const container = document.createElement("div");

    const title = document.createElement("label");
    title.classList.add(css.tuneTitle);
    title.innerHTML = "Box shadow";
    container.appendChild(title);

    const row = document.createElement("div");
    row.classList.add("d-flex", "align-items-end");

    const fields = [
      { key: "x", label: "X", allowNegative: true },
      { key: "y", label: "Y", allowNegative: true },
      { key: "blur", label: "Blur", allowNegative: false },
      { key: "spread", label: "Spread", allowNegative: true },
    ];

    fields.forEach(({ key, label: labelText, allowNegative }) => {
      const col = document.createElement("div");
      col.classList.add(css.col, "d-flex", "editor-block-tune");

      const label = document.createElement("label");
      label.classList.add(css.label, css.tuneTitle);
      label.innerHTML = labelText;

      const input = document.createElement("input");
      input.classList.add(css.input, css.tuneInput);
      input.setAttribute("type", "number");
      if (!allowNegative) input.setAttribute("min", "0");

      const current = this.data.boxShadow[key];
      if (current) input.value = current;

      input.addEventListener("input", (e) => {
        this.data.boxShadow[key] = e.target.value === "" ? 0 : e.target.value;
        this._applyBoxShadow();
        this.debounce(() => this.block.dispatchChange())();
      });

      col.appendChild(label);
      col.appendChild(input);
      row.appendChild(col);
    });

    // Inset toggle
    const insetCol = document.createElement("div");
    insetCol.classList.add("d-flex", "editor-block-tune");

    const insetLabel = document.createElement("label");
    insetLabel.classList.add(css.label, css.tuneTitle);
    insetLabel.innerHTML = "Inset";

    const insetCheckbox = document.createElement("input");
    insetCheckbox.classList.add(css.checkbox);
    insetCheckbox.setAttribute("type", "checkbox");
    insetCheckbox.checked = this.data.boxShadow.inset;

    insetCheckbox.addEventListener("change", (e) => {
      this.data.boxShadow.inset = e.target.checked;
      this._applyBoxShadow();
      this.debounce(() => this.block.dispatchChange())();
    });

    insetCol.appendChild(insetLabel);
    insetCol.appendChild(insetCheckbox);
    row.appendChild(insetCol);

    container.appendChild(row);

    // Shadow colour
    const shadowColorInput = document.createElement("input");
    shadowColorInput.classList.add(css.colorBtn);
    container.appendChild(
      this._buildColorRow("Shadow colour", shadowColorInput),
    );

    this._initColorPicker(shadowColorInput, container, {
      getVal: () => this.data.boxShadow.color,
      setVal: (v) => {
        this.data.boxShadow.color = v;
      },
      applyFn: () => this._applyBoxShadow(),
    });

    return container;
  }

  _initColorPicker(colorInput, tuneWrapper, { getVal, setVal, applyFn }) {
    const picker = new Pickr({
      el: colorInput,
      theme: "monolith",
      default: getVal(),
      // Opens to the right of the trigger swatch instead of below it
      position: "right-middle",
      swatches: this.settings?.options,
      components: {
        preview: true,
        opacity: true,
        hue: true,
        interaction: {
          hex: true,
          rgba: true,
          input: true,
          cancel: true,
          save: true,
        },
      },
    });

    picker
      .on("init", () => {
        const pickerContainer = picker.getRoot().app;

        pickerContainer.addEventListener("focusout", () => {
          const target = this._getTarget();
          if (
            target &&
            !target.closest(".ce-block")?.contains(document.activeElement) &&
            !pickerContainer.contains(document.activeElement)
          ) {
            tuneWrapper.focus?.();
          }
        });
      })
      .on("change", (color) => {
        // Live preview only — apply, then revert the stored value so nothing
        // is committed until the user hits save/swatchselect.
        const hex = color.toHEXA().toString();
        const prevVal = getVal();
        setVal(hex);
        applyFn();
        setVal(prevVal);
      })
      .on("save", (color) => {
        setVal(color.toHEXA().toString());
        applyFn();
        picker.hide();
        this.debounce(() => this.block.dispatchChange())();
      })
      .on("hide", () => {
        applyFn();
      })
      .on("cancel", () => {
        applyFn();
        picker.hide();
      })
      .on("swatchselect", (color) => {
        setVal(color.toHEXA().toString());
        applyFn();
        picker.applyColor();
        picker.hide();
        this.debounce(() => this.block.dispatchChange())();
      });

    this._pickers.push(picker);
  }

  save() {
    return this.data;
  }

  destroy() {
    this._pickers.forEach((p) => p.destroyAndRemove());
    this._pickers = [];
  }
}
